import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import Joyride, { CallBackProps, Events, EVENTS, StoreHelpers } from "react-joyride";
import { NoTourBeacon, TourTooltip, tour_steps } from "./tour";
import { courseDataToTabData, getCourseTab } from "@vixi/utils";
import useUiStore from "@/store/ui.state";
import { useCourseEditorContext } from "./context";
import CourseTabs from "./CourseTabs";
import { CourseData } from "@vixi/types";
import { useTranslation } from "@vixi/localization";
import { cn } from "@/lib/utils";

interface CourseEditorProps {
  courseData?: CourseData.Course | null;
  setCourseData: React.Dispatch<React.SetStateAction<CourseData.Course | null | undefined>>;
  mode?: "view" | "edit";
  navGroup: ReactNode;
}

const CourseEditor: FC<Partial<CourseEditorProps>> = ({ navGroup }) => {
  const { courseData, setActiveTabs } = useCourseEditorContext();
  const {
    watchedCourseTour,
    setWatchedCourseTour,
    sidebar: { setSidebarMode, mode: sidebarMode },
  } = useUiStore();
  const { isRtl } = useTranslation();

  const [stepIndex, setStepIndex] = useState(0);
  const [tourHelpers, setTourHelpers] = useState<StoreHelpers>();

  // Set the sidebar mode to compact when the component mounts, and expand it on exit
  useEffect(() => {
    setSidebarMode("compact");
    return () => {
      setSidebarMode("expanded");
    };
  }, [setSidebarMode]);

  const courseEditorData = useMemo(
    () => courseData && courseDataToTabData(courseData),
    [courseData],
  );

  const joyrideCallback = (data: CallBackProps) => {
    const { index, type } = data;

    if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as Events[]).includes(type)) {
      const nextIndex = index + 1;

      setStepIndex(nextIndex);

      if (nextIndex < tour_steps.length) {
        // Check if the next step needs a tab to be opened
        const nextStep = tour_steps[nextIndex];
        if (courseEditorData && nextStep?.data?.initialTabValues) {
          const newActiveTabs = getCourseTab(courseEditorData, nextStep.data.initialTabValues);

          if (newActiveTabs) setActiveTabs(newActiveTabs.path?.map((x) => x + 1) ?? [1, 1, 1]);
        }
      }
    } else if (type === EVENTS.BEACON) {
      // Start the tour when beacon is supposed to appear
      if (!watchedCourseTour) {
        tourHelpers?.open();
      }
    } else if (type === EVENTS.TOUR_END) {
      setWatchedCourseTour(true);
    }

    console.groupCollapsed(type);
    console.log(data); //eslint-disable-line no-console
    console.groupEnd();
  };

  return courseEditorData ? (
    <div className="m-auto flex h-[calc(100vh-100px)] max-h-175 w-full max-w-350 p-5">
      <Joyride
        spotlightClicks
        beaconComponent={NoTourBeacon}
        getHelpers={setTourHelpers}
        continuous={true}
        run={sidebarMode === "compact"}
        floaterProps={{
          hideArrow: true,
        }}
        tooltipComponent={TourTooltip}
        stepIndex={stepIndex}
        callback={joyrideCallback}
        steps={tour_steps}
      />
      {isRtl ? (
        <>
          <div className={cn("flex", isRtl ? "me-8.75" : "ms-8.75")}>{navGroup}</div>
          <CourseTabs showTabNumbers tabList={courseEditorData} />
        </>
      ) : (
        <>
          <CourseTabs showTabNumbers tabList={courseEditorData} />
          <div className={cn("flex", isRtl ? "me-8.75" : "ms-8.75")}>{navGroup}</div>
        </>
      )}
    </div>
  ) : null;
};

export { CourseEditor };
